'use client';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Clock, MapPin, Trash2, User } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { CartItem, ServiceLocation } from '@/types/service';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { formatInTimeZone } from 'date-fns-tz';

interface ServiceCartProps {
  items: CartItem[];
  location: ServiceLocation;
  onRemove: (index: number) => void;
  totalDuration: number;
  totalPrice: number;
  onProceed: () => void;
  currentStep: 'services' | 'slots' | 'form' | 'success';
}

export function ServiceCart({
  items,
  location,
  onRemove,
  totalDuration,
  totalPrice,
  onProceed,
  currentStep,
}: ServiceCartProps) {
  const formatPrice = (amount: number, currency?: string) => {
    if (amount === 0 || !currency) return 'Free';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            No services selected
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasPrice = items.some(
    (item) => item.price.amount > 0 && item.price.currency
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Selected Services</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4" />
            <div>
              <div>{location.name}</div>
              <div>
                {location.address}, {location.city}
              </div>
              <div>
                {location.state} {location.zip_code}
              </div>
            </div>
          </div>
        </div>
        <Separator className="mb-6" />
        <div className="space-y-4">
          {items.map((item, index) => (
            <div key={index}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="font-medium">{item.service.name}</div>
                  <div className="text-sm text-muted-foreground flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {item.price.duration} min
                  </div>
                  {item.staff && (
                    <div className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                      <User className="w-4 h-4" />
                      {item.staff.name === ''
                        ? 'Any Available Staff'
                        : item.staff.name}
                    </div>
                  )}
                  {item.slot && (
                    <Badge variant="secondary" className="mt-2">
                      {formatInTimeZone(
                        item.slot.start_at,
                        item.slot.time_zone,
                        'd MMM, h:mm a'
                      )}
                    </Badge>
                  )}
                  {!item.slot && currentStep === 'slots' && (
                    <Badge variant={'destructive'}>No slot selected</Badge>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-medium">
                    {formatPrice(item.price.amount, item.price.currency)}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onRemove(index)}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
              {index < items.length - 1 && <Separator className="my-4" />}
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex-col gap-4">
        <div className="w-full flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Total Duration
          </div>
          <div>{totalDuration} min</div>
        </div>
        {hasPrice && (
          <div className="w-full flex items-center justify-between font-medium">
            <div>Total</div>
            <div>
              {formatPrice(
                totalPrice,
                items.find((item) => item.price.currency)?.price.currency
              )}
            </div>
          </div>
        )}
        <Button
          className="w-full"
          onClick={onProceed}
          disabled={items.length === 0}
        >
          Proceed to Book
        </Button>
      </CardFooter>
    </Card>
  );
}
